import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveTempWorkerRequest, CreateTempWorkerRequest, GetTempWorkerRequest, GetTempWorkersRequest, SyncTempWorkerRequest, UpdateTempWorkerRequest, GetExportRequest } from "./api";

type Props = {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

const useCreateTempWorker = (props?: Props) => {
    const queryClient = useQueryClient();
    const {mutate, isLoading, isError, error} = useMutation(CreateTempWorkerRequest, {
        onSuccess: (data: any) => {
            queryClient.invalidateQueries(['temp_workers']);
            props?.onSuccess && props.onSuccess(data);
        },
        onError: props?.onError,
    });

    return {mutate, isLoading, isError, error};
};

const useArchiveTempWorker = (props?: Props) => {
    const queryClient = useQueryClient();
    const {mutate, isLoading, isError, error} = useMutation(ArchiveTempWorkerRequest, {
        onSuccess: (data: any) => {
            queryClient.invalidateQueries(['temp_workers']);
            props?.onSuccess && props.onSuccess(data);
        },
        onError: props?.onError,
    });

    return {mutate, isLoading, isError, error};
};

const useSyncTempWorker = (props?: Props) => {
    const queryClient = useQueryClient();
    const {mutate, isLoading, isError, error} = useMutation(SyncTempWorkerRequest, {
        onSuccess: (data: any) => {
            queryClient.invalidateQueries(['temp_workers']);
            props?.onSuccess && props.onSuccess(data);
        },
        onError: props?.onError,
    });

    return {mutate, isLoading, isError, error};
};

const useUpdateTempWorker = (props?: Props) => {
    const queryClient = useQueryClient();
    const {mutate, isLoading, isError, error} = useMutation(UpdateTempWorkerRequest, {
        onSuccess: (data: any) => {
            queryClient.invalidateQueries(['temp_workers']);
            props?.onSuccess && props.onSuccess(data);
        },
        onError: props?.onError,
    });

    return {mutate, isLoading, isError, error};
};

const useGetTempWorker = (tempWorkerId: number, props?: Props) => {
    const {data, isLoading, isError} = useQuery<any>(['temp_workers', `${tempWorkerId}`], () => GetTempWorkerRequest(tempWorkerId), {
        onSuccess: props?.onSuccess,
        onError: props?.onError,
    });

    return {data, isLoading, isError};
};

type TempWorkerProps = {
    archived?: boolean,
    synced?: boolean,
    retours?: boolean,
}

const useGetTempWorkers = (params: TempWorkerProps, props?: Props) => {
    const {data, status, fetchNextPage, hasNextPage, isFetchingNextPage} = useInfiniteQuery({
        queryKey: ['temp_workers', params],
        queryFn: async ({pageParam = 1}) => GetTempWorkersRequest({...params, page: pageParam}),
        getNextPageParam: (lastPage, _pages) => lastPage.info.next_page !== null ? lastPage.info.next_page : undefined,
        onError: props?.onError,
        onSuccess: props?.onSuccess,
    });

    return {data, status, fetchNextPage, hasNextPage, isFetchingNextPage};
};

const useGetRetoursCandidatsCount = (props?: Props) => {
    const { data } = useQuery(
        ['retours_candidats_count'],
        () => GetTempWorkersRequest({ retours: true, archived: false, page: 1 }),
        { onError: props?.onError }
    );
    return data?.info?.total_count ?? 0;
};

const useGetExport = (exportId: number) => {
  return useQuery(['exports', exportId], () => GetExportRequest(exportId));
};

export { useCreateTempWorker, useArchiveTempWorker, useSyncTempWorker, useUpdateTempWorker, useGetTempWorker, useGetTempWorkers, useGetRetoursCandidatsCount, useGetExport };